import { useState, useEffect, useCallback, useRef, useReducer } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  signInAnonymously,
  signOut,
  linkWithPopup,
  linkWithCredential,
  signInWithEmailLink,
  isSignInWithEmailLink,
  EmailAuthProvider,
} from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';

// We invoke the custom magic-link Cloud Function via a Firebase Hosting
// rewrite (`/api/sendMagicLinkEmail` → the function). This is same-origin
// so no CORS is involved — historically the simpler httpsCallable path
// hit CORS issues with Gen 2 callables on a custom domain.
const MAGIC_LINK_ENDPOINT = '/api/sendMagicLinkEmail';

// Local-storage key used by the magic-link round trip. Firebase requires us
// to remember the email we sent the link to, because the click happens in a
// different page load (or device) than the request, and the email isn't
// embedded in the link for security reasons.
const PENDING_EMAIL_KEY = 'lists_pending_magic_email';

// BroadcastChannel name for cross-tab auth-state notifications. When
// the magic-link completion handler fires linkWithCredential or
// signInWithEmailLink in one tab, we post a message here so any other
// open listicity tab can refresh its currentUser snapshot — Firebase's
// own cross-tab sync via IndexedDB is unreliable on Android Chrome
// (storage partitioning). The channel is constructed lazily inside
// effects and closed on cleanup so the API isn't required (older
// browsers without BroadcastChannel just rely on visibilitychange +
// focus fallbacks below).
const AUTH_BC_NAME = 'listicity-auth';

// Module-scope guard against React-Strict-Mode's double-mount in dev.
// StrictMode mounts effects twice (mount → unmount → mount), which used to
// fire signInWithEmailLink twice — the first call consumed the one-time
// OOB code and the second came back "expired". A useRef wouldn't help
// here because the ref is reset on the unmount/remount cycle. A module-
// level flag survives across remounts within the same page load, which
// is exactly what we want — Firebase sign-in links are single-use, so a
// repeat completion attempt is always a bug.
let MAGIC_LINK_COMPLETION_STARTED = false;

// Settings passed to sendSignInLinkToEmail. The url is where Firebase will
// redirect the user after they click the link in their email — must be a
// domain we've added to Firebase Auth → Authorized domains. We use the
// current origin so the same code works on walterstodotool.web.app,
// listicity.app, and localhost in dev.
function buildActionCodeSettings() {
  return {
    url: window.location.origin + window.location.pathname,
    handleCodeInApp: true,
  };
}

/**
 * useAuth — auth state + the three sign-in methods + anonymous-to-real
 * conversion paths. See LoginPage and AccountPopover for the consumers.
 *
 * Returned shape:
 *   user                    Firebase user, null (signed out), or undefined (loading)
 *   magicLinkProcessing     true while we're completing a magic-link return
 *
 *   signInWithGoogle()      Google OAuth popup
 *   signInAsGuest()         Anonymous user (no email, real UID)
 *   sendMagicLink(email)    Sends the email; user finishes by clicking the link
 *
 *   linkToGoogle()          Convert current anonymous user to a Google account
 *   linkToMagicLink(email)  Convert current anonymous user via magic link
 *
 *   signOut()
 *
 * The magic-link conversion vs new-sign-in decision happens automatically
 * inside the return-URL handler — we sniff `auth.currentUser?.isAnonymous`
 * and call linkWithCredential or signInWithEmailLink accordingly. So
 * `sendMagicLink` and `linkToMagicLink` are the same function; the only
 * difference is intent on the calling side.
 */
export function useAuth() {
  // undefined while initial auth state is being restored from IndexedDB,
  // null once we've confirmed signed-out, or a real Firebase user.
  const [user, setUser] = useState(undefined);
  // True while we're completing a magic-link return — App.js shows a
  // "signing in…" splash instead of the login page during this window.
  const [magicLinkProcessing, setMagicLinkProcessing] = useState(
    typeof window !== 'undefined' && isSignInWithEmailLink(auth, window.location.href),
  );

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return unsub;
  }, []);

  // Walter 2026-05-25 (Bug #7 — round-2 tester report) + 2026-05-26
  // hardening: magic-link sign-in completes in tab B but tab A still
  // shows guest. Firebase's cross-tab onAuthStateChanged sync via
  // IndexedDB is unreliable on Android Chrome (storage partitioning).
  //
  // Original fix added a visibilitychange listener that called
  // user.reload() on return-to-foreground. Two limitations of that
  // fix surfaced in real testing:
  //   1. reload() mutates the Firebase User object IN PLACE — the
  //      reference doesn't change. When the main onAuthStateChanged
  //      listener fails to fire, calling setUser(auth.currentUser)
  //      passes React the same reference; useState's Object.is check
  //      skips the re-render even though isAnonymous flipped from
  //      true → false on the user object.
  //   2. visibilitychange only fires when the tab loses + regains
  //      visibility. It doesn't fire on window blur/focus, doesn't
  //      fire if the magic-link click happens to be in the same tab,
  //      and may not fire reliably across all Chrome / Safari builds.
  //
  // Stronger fix:
  //   - useReducer-based forceRefresh counter that React can't
  //     short-circuit (useReducer always re-renders, even with
  //     identical state). After reload(), bump the counter — the
  //     parent component re-evaluates user.isAnonymous against the
  //     in-place-mutated User object and the UI catches up.
  //   - Multiple refresh triggers: visibilitychange, window focus,
  //     and an explicit BroadcastChannel message. The magic-link
  //     completion handler posts to the channel as soon as
  //     linkWithCredential/signInWithEmailLink succeeds, so any
  //     other open listicity tab refreshes directly without waiting
  //     for storage-partitioning sync.
  // eslint-disable-next-line no-unused-vars
  const [refreshTick, forceRefresh] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    let bc;
    const refresh = async () => {
      const u = auth.currentUser;
      if (!u) {
        // No current user — bump anyway in case state went from
        // "signed in" to "signed out" via another tab.
        forceRefresh();
        return;
      }
      try { await u.reload(); } catch { /* best-effort */ }
      // Force a re-render regardless of whether onAuthStateChanged
      // re-emitted. user.isAnonymous and provider state are read on
      // every render by consumers, so a forced render picks up the
      // post-reload values.
      forceRefresh();
    };
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refresh();
    };
    const onFocus = () => refresh();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    // BroadcastChannel may not exist (Safari old, some embedded
    // contexts). Guard the construction. If it works, any tab that
    // posts `{ type: 'auth-changed' }` triggers refresh here.
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel(AUTH_BC_NAME);
        bc.onmessage = (ev) => {
          if (ev?.data?.type === 'auth-changed') refresh();
        };
      }
    } catch { /* fail-open */ }
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      try { bc?.close(); } catch {}
    };
  }, []);

  // Magic-link return handler. Runs once on mount. If the current URL is a
  // Firebase sign-in link, wait for auth state to settle (so we know whether
  // there's an anonymous user to convert), then complete the flow.
  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    // StrictMode-safe: only the first effect-mount in this page load
    // actually does the work. Any subsequent remount (caused by
    // StrictMode's double-mount in dev, or by a hot reload during this
    // flow) sees the flag and bails — otherwise the second
    // signInWithEmailLink call would land on an already-consumed code
    // and surface "expired" to the user.
    if (MAGIC_LINK_COMPLETION_STARTED) return;
    MAGIC_LINK_COMPLETION_STARTED = true;

    // One-shot subscription to grab the first emitted auth state. We can't
    // use auth.currentUser directly on mount because IndexedDB hasn't been
    // read yet — currentUser is null until the persistence layer rehydrates.
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      unsub();

      let email = window.localStorage.getItem(PENDING_EMAIL_KEY);
      if (!email) {
        // Edge case: user clicked the link in a different browser than
        // where they requested it, so localStorage doesn't have the email.
        // Firebase requires it to complete sign-in — prompt for it.
        email = window.prompt(
          'Please confirm the email you used to request the sign-in link',
        );
      }
      if (!email) {
        setMagicLinkProcessing(false);
        return;
      }

      try {
        if (currentUser && currentUser.isAnonymous) {
          // Guest → real user. Try to upgrade the anonymous account IN
          // PLACE first (preserves the uid, so the guest's boards/lists
          // carry over). If the email already belongs to a real account,
          // linking throws — Firebase uses BOTH `auth/email-already-in-use`
          // AND `auth/credential-already-in-use` depending on the path —
          // so fall back to signing into the EXISTING account.
          //
          // Walter 2026-06-02: this is the returning-user case (e.g.
          // opening the link in a fresh/incognito window where a throwaway
          // guest exists). Previously `email-already-in-use` was unhandled
          // and dead-ended at "invalid or expired" — magic-link sign-in
          // is supposed to work cross-browser/device, and now does.
          try {
            const credential = EmailAuthProvider.credentialWithLink(
              email,
              window.location.href,
            );
            await linkWithCredential(currentUser, credential);
          } catch (linkErr) {
            if (
              linkErr.code === 'auth/email-already-in-use' ||
              linkErr.code === 'auth/credential-already-in-use'
            ) {
              const ok = window.confirm(
                'This email already has a listicity account.\n\n' +
                'Signing in won’t bring over the lists you made as a guest in this window.\n\n' +
                'To keep those guest lists, click Cancel, then either:\n' +
                '• export them (account menu → import / export) and re-import after you sign in, or\n' +
                '• sign in with a different email — that saves them as their own account.\n\n' +
                'To sign in and leave the guest lists behind, click OK.',
              );
              if (!ok) return; // outer finally still clears the processing flag
              // Sign into the existing account. (email-already-in-use does
              // not consume the one-time code, so this link is still valid.)
              await signInWithEmailLink(auth, email, window.location.href);
            } else {
              throw linkErr;
            }
          }
        } else {
          // Fresh sign-in (no existing anonymous user, or already a real one).
          await signInWithEmailLink(auth, email, window.location.href);
        }
        window.localStorage.removeItem(PENDING_EMAIL_KEY);
        // Scrub the magic-link query params from the URL so a refresh
        // doesn't try to reuse an already-consumed code.
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
        // Walter 2026-05-26: broadcast the auth change to any other
        // open listicity tabs so they refresh their currentUser
        // snapshot without waiting for storage-partitioning sync.
        // Same-tab consumers don't need this — Firebase will fire
        // onAuthStateChanged locally — but cross-tab is what was
        // failing in the bug report. One-shot post + close; the
        // useEffect above handles incoming messages.
        try {
          if (typeof BroadcastChannel !== 'undefined') {
            const bc = new BroadcastChannel(AUTH_BC_NAME);
            bc.postMessage({ type: 'auth-changed', at: Date.now() });
            // Defer close so the message has time to leave the queue.
            setTimeout(() => { try { bc.close(); } catch {} }, 100);
          }
        } catch { /* fail-open */ }
      } catch (e) {
        console.error('Magic-link sign-in failed:', e?.code, e);
        if (e.code === 'auth/expired-action-code') {
          // Genuinely past the 1-hour window.
          window.alert('This sign-in link has expired — links are only valid for 1 hour. Please request a new one.');
        } else if (e.code === 'auth/invalid-action-code') {
          // One-time code already consumed (link opened/clicked more than
          // once, or pre-fetched by an email scanner) or otherwise invalid.
          window.alert('This sign-in link was already used or is no longer valid. Please request a new one, and open it only once (clicking it a second time won\'t work).');
        } else if (e.code === 'auth/invalid-email') {
          window.alert('This link was issued for a different email than the one saved on this device. Request a new link and open it in the same browser where you entered your email.');
        } else {
          window.alert('Sign-in link is invalid or expired. Please request a new one.' + (e?.code ? `\n\n(error: ${e.code})` : ''));
        }
      } finally {
        setMagicLinkProcessing(false);
      }
    });
  }, []);

  const signInWithGoogle = useCallback(
    () => signInWithPopup(auth, googleProvider),
    [],
  );

  // Walter 2026-05-16: GIS-based Google sign-in. Pass a Google ID
  // token (obtained from google.accounts.id in the LoginPage) and
  // we mint a Firebase Auth session via signInWithCredential. No
  // popup, no redirect, no sessionStorage state — bypasses the
  // Android Chrome storage-partition issue that broke
  // signInWithPopup. Falls back through useAuth's normal
  // onAuthStateChanged → onSignIn pipeline once the credential
  // resolves, so consent-flush + welcome-template seeding still
  // work the same way.
  const signInWithGoogleIdToken = useCallback(async (idToken) => {
    if (!idToken) throw new Error('signInWithGoogleIdToken: idToken is required');
    const credential = GoogleAuthProvider.credential(idToken);
    return signInWithCredential(auth, credential);
  }, []);

  const signInAsGuest = useCallback(() => signInAnonymously(auth), []);

  const sendMagicLink = useCallback(async (email, returnUrlOverride) => {
    // POSTs to /api/sendMagicLinkEmail which Firebase Hosting rewrites to
    // the Cloud Function. The body shape matches the onCall envelope
    // ({ data: ... }) so the function reads request.data the same way it
    // would via httpsCallable.
    //
    // Default: land back at the app root. App.js will route the now-authed
    // user to their default board. Callers like AcceptInvitePage pass an
    // override so the round-trip preserves their specific path (e.g.
    // /accept-invite/{token}) — the page needs to re-render to fire its
    // post-signin work.
    const returnUrl = returnUrlOverride || (window.location.origin + '/');
    const res = await fetch(MAGIC_LINK_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: { email, returnUrl } }),
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      // Cloud Functions Gen 2 callable error body is JSON shaped like:
      // { "error": { "message": "account-suspended", "status": "PERMISSION_DENIED" } }
      // We sniff the message for our suspended-account discriminator
      // so the LoginPage can show a useful explainer instead of the
      // generic "could not send" copy. Anything else falls through
      // to a plain Error with status info.
      let parsed;
      try { parsed = JSON.parse(body); } catch { parsed = null; }
      const inner = parsed?.error?.message || '';
      if (inner === 'account-suspended') {
        const err = new Error('account-suspended');
        err.code = 'account-suspended';
        throw err;
      }
      throw new Error(`Magic link request failed: ${res.status} ${body}`);
    }
    window.localStorage.setItem(PENDING_EMAIL_KEY, email);
  }, []);

  // Conversion paths. linkWithPopup throws auth/credential-already-in-use if
  // the Google account already has its own listicity user — the AccountPopover
  // catches that and offers a graceful fallback.
  const linkToGoogle = useCallback(async () => {
    if (!auth.currentUser) throw new Error('No current user to link');
    return linkWithPopup(auth.currentUser, googleProvider);
  }, []);

  // Magic-link conversion uses the same outbound call as a fresh request —
  // the conversion-vs-sign-in decision is made in the return handler based
  // on whether currentUser is anonymous at that moment.
  const linkToMagicLink = sendMagicLink;

  const signOut_ = useCallback(() => signOut(auth), []);

  return {
    user,
    magicLinkProcessing,
    // Back-compat alias for the old single sign-in function.
    signIn: signInWithGoogle,
    signInWithGoogle,
    signInWithGoogleIdToken,
    signInAsGuest,
    sendMagicLink,
    linkToGoogle,
    linkToMagicLink,
    signOut: signOut_,
  };
}
